import React, { Component } from 'react';
import PropTypes from 'prop-types';

/**
 * ErrorBoundary - Catches JavaScript errors in child components
 *
 * Prevents the entire Gigabot from crashing when a child component throws.
 * Displays a fallback UI and logs errors for debugging.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[Gigabot ErrorBoundary] Caught error:', error);
    console.error('[Gigabot ErrorBoundary] Error info:', errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='gigabot-error-boundary'>
          <div className='error-content'>
            <span className='error-icon'>⚠️</span>
            <p className='error-message'>
              {this.props.errorMessage ||
                'Something went wrong displaying this content.'}
            </p>
            {this.props.showRetry && (
              <button
                className='error-retry-btn'
                onClick={this.handleRetry}
                type='button'
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  errorMessage: PropTypes.string,
  showRetry: PropTypes.bool,
  onError: PropTypes.func,
};

ErrorBoundary.defaultProps = {
  fallback: null,
  errorMessage: 'Something went wrong displaying this content.',
  showRetry: true,
  onError: null,
};

export default ErrorBoundary;
